import { Text, TextColor, TextVariant } from '@vizit/atoms';
import { ScoreColorVariant } from '@vizit/constants';
import { PlusIcon, XCloseIcon, ZapCompactIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import { getScoreColorVariant } from '@vizit/utils';
import * as React from 'react';
import { Heading, HeadingVariant } from '../heading';
import type { ScoreWeightItem, ScoreWeightsPanelProps } from './types';

/**
 * One legend entry sized to its bar segment's share. A narrow segment can't fit its
 * label, and a partial clip ("ASSET ORD") reads worse than no label — so when the
 * bolt + label overflow the entry, the label collapses to just the bolt. The label
 * stays in the layout (visibility, not removal) so the overflow measurement is
 * stable: removing it would end the overflow and immediately bring it back.
 */
function LegendItem({ item, isFaded }: { item: ScoreWeightItem; isFaded: boolean }) {
  const innerRef = React.useRef<HTMLDivElement>(null);
  const labelRef = React.useRef<HTMLDivElement>(null);
  const [isLabelHidden, setIsLabelHidden] = React.useState(false);

  React.useLayoutEffect(() => {
    const inner = innerRef.current;
    const label = labelRef.current;
    if (inner == null || label == null || typeof ResizeObserver === 'undefined') return;
    // The label is the element that actually clips (it shrinks inside the flex row and
    // cuts its own content), so the fit test must compare ITS content width against ITS
    // box — the container's scrollWidth never grows. Observe the container for resizes.
    const measure = () => setIsLabelHidden(label.scrollWidth > label.clientWidth + 1);
    const observer = new ResizeObserver(measure);
    observer.observe(inner);
    measure();
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className={cx('min-w-0 basis-0 transition-opacity duration-350', isFaded && 'opacity-20')}
      style={{ flexGrow: item.weightPct }}
      data-testid="score-weights-legend-item"
      data-key={item.key}
      data-faded={isFaded ? 'true' : 'false'}
      data-label-hidden={isLabelHidden ? 'true' : 'false'}
    >
      <div ref={innerRef} className="flex items-center gap-2 overflow-hidden whitespace-nowrap">
        <span className={cx('shrink-0', item.accentClassName)}>
          <ZapCompactIcon size={14} aria-hidden="true" />
        </span>
        <Text
          ref={labelRef}
          variant={TextVariant.MonoXs}
          color={TextColor.Quaternary}
          showEllipsis={false}
          className={cx(isLabelHidden && 'invisible')}
        >
          {item.label}
        </Text>
      </div>
    </div>
  );
}

// Plain-weight suffixes appended to a segment's value while its metric is isolated.
const SUFFIX_WEIGHT = 'overall impact';
const SUFFIX_EARNED = 'current impact';
const SUFFIX_AVAILABLE = 'additional opportunity';
// The "+Npts additional opportunity" labels on the lighter segments are hidden for now; the
// segments still show the headroom. Flip to bring the labels back.
const SHOW_ADDITIONAL_OPPORTUNITY = false;

/** A bar segment's value ("33%", "15pts") with its suffix shown only while that metric is isolated. */
function SegmentLabel(props: { value: string; suffix: string; showSuffix: boolean; className?: string }) {
  const { value, suffix, showSuffix, className } = props;
  return (
    <span className={cx('px-4 font-sans text-sm', className)}>
      {value}
      {showSuffix && <span className="font-mono font-regular text-xxs uppercase">{` ${suffix}`}</span>}
    </span>
  );
}

const TITLE = 'Product page scoring formula';
const TOTAL_CLASSES = 'flex h-16 w-16 shrink-0 items-center justify-center font-mono text-sm font-medium';
// The product page score card's tier surface and text, so the total reads as that score.
const TOTAL_COLOR_CLASSES: Record<ScoreColorVariant, string> = {
  [ScoreColorVariant.NotApplicable]: 'bg-score-not-applicable text-score-not-applicable-strong',
  [ScoreColorVariant.VeryLow]: 'bg-score-very-low text-score-very-low-strong',
  [ScoreColorVariant.Low]: 'bg-score-low text-score-low-strong',
  [ScoreColorVariant.Moderate]: 'bg-score-moderate text-score-moderate-strong',
  [ScoreColorVariant.High]: 'bg-score-high text-score-high-strong',
  [ScoreColorVariant.VeryHigh]: 'bg-score-very-high text-score-very-high-strong',
};
const SUBTITLE =
  "Your Product Page score combines each component using weights derived from Vizit's intelligence on how real shoppers see and respond to product pages. Each component is scored 0–100, and its weight (%) shows how much it impacts your overall score.";

/**
 * Expanded content of the "Score weights" disclosure on the product page performance
 * section: title + subtitle explaining the weighted average, a 100%-stacked bar whose
 * segment widths are the metric weights, and a color legend. Purely presentational —
 * isolation is driven via props.
 *
 * @example
 * <ScoreWeightsPanel items={items} isolatedKey="hero" />
 */
const ScoreWeightsPanel = React.forwardRef<HTMLDivElement, ScoreWeightsPanelProps>((props, forwardedRef) => {
  const { items, isolatedKey = null, total = null, onMetricHover, className, ...other } = props;

  // Tracks whether the pointer is on one of this panel's segments. If the panel unmounts
  // mid-hover (e.g. a refetch puts the row into loading), mouseleave never fires, so the
  // cleanup reports the leave itself; otherwise the parent keeps a phantom isolation.
  const isHoveringRef = React.useRef(false);
  const onMetricHoverRef = React.useRef(onMetricHover);
  onMetricHoverRef.current = onMetricHover;
  React.useEffect(
    () => () => {
      if (isHoveringRef.current) onMetricHoverRef.current?.(null);
    },
    [],
  );

  // The formula term holding keyboard focus, if any. Mouse leave falls back to it instead of
  // clearing, so moving the pointer away doesn't drop isolation under a visible focus ring.
  const focusedKeyRef = React.useRef<string | null>(null);

  const metricHoverHandlers = (key: string) =>
    onMetricHover != null
      ? {
          onMouseEnter: () => {
            isHoveringRef.current = true;
            onMetricHover(key);
          },
          onMouseLeave: () => {
            isHoveringRef.current = focusedKeyRef.current != null;
            onMetricHover(focusedKeyRef.current);
          },
        }
      : {};

  // Formula terms also isolate from the keyboard, like the metric cards: focusable, and focus/blur
  // report the same isolation as mouse enter/leave. Blur only clears the isolation this term set,
  // so it can't un-isolate a metric the pointer has since moved onto.
  const formulaTermHandlers = (key: string) =>
    onMetricHover != null
      ? {
          ...metricHoverHandlers(key),
          tabIndex: 0,
          onFocus: () => {
            focusedKeyRef.current = key;
            isHoveringRef.current = true;
            onMetricHover(key);
          },
          onBlur: () => {
            focusedKeyRef.current = null;
            if (isolatedKey !== key) return;
            isHoveringRef.current = false;
            onMetricHover(null);
          },
        }
      : {};

  const isFaded = (key: string) => isolatedKey != null && isolatedKey !== key;
  const isIsolated = (key: string) => isolatedKey === key;
  const hasAllScores = items.length > 0 && items.every((item) => item.score != null);
  const hasAllPoints = items.length > 0 && items.every((item) => item.points != null && item.pointsToGo != null);

  const isolatedItem = items.find((item) => item.key === isolatedKey && item.score != null);
  // Falls back to the item's `label` (always present) when `insightLabel` (sentence-case,
  // BE/config-provided) is missing, so the insight line never renders an empty `<strong>`.
  const insightLabel = isolatedItem != null ? (isolatedItem.insightLabel ?? isolatedItem.label) : null;
  // The metric's name without a trailing "score", so copy can say "your <name> score" for every metric.
  const metricName = insightLabel?.replace(/ score$/i, '') ?? null;

  const barsRef = React.useRef<HTMLDivElement>(null);
  const [popOutStyle, setPopOutStyle] = React.useState<React.CSSProperties | null>(null);
  const popOutItem = popOutStyle != null ? items.find((item) => item.key === isolatedKey) : undefined;
  // While a metric's pop-out is showing, its own in-bar labels would only repeat it, so they
  // hide — by visibility, not removal, so the truncation measurement above stays stable.
  const isLabelInPopOut = (key: string) => popOutItem?.key === key;

  // What the segment widths and label text depend on, so served data that changes while a
  // metric stays isolated (a refetch) re-measures too. A string, not `items`, because the
  // parent rebuilds that array on every render.
  const barsLayoutKey = items
    .map((item) => `${item.key}:${item.weightPct}:${item.points}:${item.pointsToGo}`)
    .join('|');

  // The pop-out only earns its place when the isolated metric's own segment labels
  // truncate (narrow weight share), so measure the real spans after the isolated
  // suffixes render. Anchored to the segment's edge nearest the panel's center so the
  // floating card never overflows the panel. Re-measured when the bars resize, since a
  // narrower panel can start (or stop) truncating and moves the segment's edges.
  // biome-ignore lint/correctness/useExhaustiveDependencies: barsLayoutKey is the trigger, not a value the effect reads
  React.useLayoutEffect(() => {
    const bars = barsRef.current;
    if (bars == null || isolatedKey == null) {
      setPopOutStyle(null);
      return;
    }
    const measure = () => {
      const spans = bars.querySelectorAll(`[data-key="${isolatedKey}"] span`);
      const isTruncated = [...spans].some((span) => span.scrollWidth > span.clientWidth + 1);
      const selector = `[data-testid="score-weights-segment"][data-key="${isolatedKey}"]`;
      const segment = bars.querySelector<HTMLElement>(selector);
      if (!isTruncated || segment == null) {
        setPopOutStyle(null);
        return;
      }
      const isInRightHalf = segment.offsetLeft + segment.offsetWidth / 2 > bars.clientWidth / 2;
      setPopOutStyle(
        isInRightHalf
          ? { right: bars.clientWidth - segment.offsetLeft - segment.offsetWidth }
          : { left: segment.offsetLeft },
      );
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(bars);
    return () => observer.disconnect();
  }, [isolatedKey, barsLayoutKey]);

  return (
    <div
      ref={forwardedRef}
      className={cx('flex flex-col gap-6 bg-primary shadow-lg-soft p-8', className)}
      data-testid="score-weights-panel"
      {...other}
    >
      <div className="flex flex-col gap-4 border-b border-secondary pb-8">
        <Heading variant={HeadingVariant.H6}>{TITLE}</Heading>
        {/* Capped so the second sentence starts on its own line at full width. */}
        <Text variant={TextVariant.SmRegular} color={TextColor.Tertiary} showEllipsis={false} className="max-w-[65rem]">
          {SUBTITLE}
        </Text>
      </div>

      <div className="flex h-16 items-center" aria-live="polite">
        {isolatedItem != null && isolatedItem.pointsToGo != null && (
          <Text
            variant={TextVariant.SmRegular}
            color={TextColor.Tertiary}
            showEllipsis={false}
            className="[&_strong]:font-semibold"
            data-testid="score-weights-insight"
          >
            {isolatedItem.pointsToGo > 0 ? (
              <>
                You could potentially increase your overall Product Page score by{' '}
                <strong className={isolatedItem.accentClassName}>
                  {isolatedItem.pointsToGo} {isolatedItem.pointsToGo === 1 ? 'point' : 'points'}
                </strong>{' '}
                by improving your <strong>{metricName} score</strong>.
              </>
            ) : isolatedItem.score === 100 ? (
              <>
                Congrats! Your <strong>{metricName} score</strong> is fully optimized and adds{' '}
                {isolatedItem.points != null ? (
                  <>
                    its full{' '}
                    <strong className={isolatedItem.accentClassName}>
                      {isolatedItem.points} {isolatedItem.points === 1 ? 'point' : 'points'}
                    </strong>
                  </>
                ) : (
                  'its maximum'
                )}{' '}
                to your overall Product Page score.
              </>
            ) : (
              // Server-side rounding can allocate all of a metric's weight points below a
              // 100 score (5% weight at score 92 serves points 5, points_to_go 0), so a
              // maxed-points metric is not necessarily a maxed score: offer the
              // improvement without promising page-score points.
              <>
                You could potentially increase your overall Product Page score by improving your{' '}
                <strong>{metricName} score</strong>.
              </>
            )}
          </Text>
        )}
      </div>

      <div className="flex w-full gap-1">
        {items.map((item) => (
          <LegendItem key={item.key} item={item} isFaded={isFaded(item.key)} />
        ))}
      </div>

      <div ref={barsRef} className="relative flex flex-col">
        {popOutItem != null && popOutStyle != null && (
          // Escape hatch for narrow segments: the isolated metric's suffix labels can
          // truncate inside their own share of the bar, so a floating copy of just that
          // metric's segments renders above the bars, each segment kept proportional
          // (flexGrow) but never narrower than its text (min-w-max). Decorative
          // duplication: hidden from AT, transparent to the pointer.
          <div
            className="pointer-events-none absolute -top-2 z-10 flex w-max max-w-full -translate-y-full flex-col gap-1 bg-primary p-3 shadow-lg"
            style={popOutStyle}
            data-testid="score-weights-popout"
            aria-hidden="true"
          >
            <div className={cx('flex h-12 items-center', popOutItem.barClassName ?? popOutItem.segmentClassName)}>
              <SegmentLabel
                value={`${popOutItem.weightPct}%`}
                suffix={SUFFIX_WEIGHT}
                showSuffix
                className="whitespace-nowrap font-bold text-white"
              />
            </div>
            {popOutItem.points != null && popOutItem.pointsToGo != null && (
              <div className="flex h-12">
                <div
                  className={cx('flex min-w-max items-center', popOutItem.segmentClassName)}
                  style={{ flexGrow: popOutItem.points }}
                >
                  <SegmentLabel
                    value={`${popOutItem.points}pts`}
                    suffix={SUFFIX_EARNED}
                    showSuffix
                    className="whitespace-nowrap font-medium text-white"
                  />
                </div>
                {popOutItem.pointsToGo > 0 && (
                  <div
                    className={cx('flex min-w-max items-center', popOutItem.availableClassName)}
                    style={{ flexGrow: popOutItem.pointsToGo }}
                  >
                    {SHOW_ADDITIONAL_OPPORTUNITY && (
                      <SegmentLabel
                        value={`+${popOutItem.pointsToGo}pts`}
                        suffix={SUFFIX_AVAILABLE}
                        showSuffix
                        className={cx('whitespace-nowrap font-medium', popOutItem.availableTextClassName)}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <div className="flex h-12 w-full gap-1">
          {items.map((item) => (
            <div
              key={item.key}
              className={cx(
                'min-w-0 basis-0 flex items-center transition-opacity duration-350',
                item.barClassName ?? item.segmentClassName,
                isFaded(item.key) && 'opacity-20',
              )}
              style={{ flexGrow: item.weightPct }}
              data-testid="score-weights-segment"
              data-key={item.key}
              data-faded={isFaded(item.key) ? 'true' : 'false'}
              data-label-hidden={isLabelInPopOut(item.key) ? 'true' : 'false'}
              {...metricHoverHandlers(item.key)}
            >
              <SegmentLabel
                value={`${item.weightPct}%`}
                suffix={SUFFIX_WEIGHT}
                showSuffix={isIsolated(item.key)}
                className={cx('truncate font-bold text-white', isLabelInPopOut(item.key) && 'invisible')}
              />
            </div>
          ))}
        </div>

        {hasAllPoints && (
          <div className="flex h-12 w-full gap-1">
            {items.map((item) => {
              const earned = item.points ?? 0;
              const pointsToGo = item.pointsToGo ?? 0;
              const earnedValue = `${earned}pts`;
              return (
                <div
                  key={item.key}
                  className={cx(
                    'min-w-0 basis-0 flex overflow-hidden transition-opacity duration-350',
                    isFaded(item.key) && 'opacity-20',
                  )}
                  style={{ flexGrow: item.weightPct }}
                  data-testid="score-weights-split"
                  data-key={item.key}
                  data-faded={isFaded(item.key) ? 'true' : 'false'}
                  data-label-hidden={isLabelInPopOut(item.key) ? 'true' : 'false'}
                  {...metricHoverHandlers(item.key)}
                >
                  {/* Both halves split by points alone (basis-0), so the label never shifts the split. The
                      earned half widens to fit its short "Npts" value (never the isolated suffix) in both
                      states, so hovering doesn't move the split; a suffix that can't fit moves to the pop-out. */}
                  <div
                    className={cx('flex basis-0 items-center overflow-hidden font-sans text-sm', item.segmentClassName)}
                    style={{ flexGrow: earned, minWidth: `calc(${earnedValue.length}ch + 16px)` }}
                  >
                    <SegmentLabel
                      value={earnedValue}
                      suffix={SUFFIX_EARNED}
                      showSuffix={isIsolated(item.key)}
                      className={cx('truncate font-medium text-white', isLabelInPopOut(item.key) && 'invisible')}
                    />
                  </div>
                  {pointsToGo > 0 && (
                    <div
                      className={cx('flex min-w-0 basis-0 items-center overflow-hidden', item.availableClassName)}
                      style={{ flexGrow: pointsToGo }}
                    >
                      {SHOW_ADDITIONAL_OPPORTUNITY && (
                        <SegmentLabel
                          value={`+${pointsToGo}pts`}
                          suffix={SUFFIX_AVAILABLE}
                          showSuffix={isIsolated(item.key)}
                          className={cx(
                            'truncate font-medium',
                            item.availableTextClassName,
                            isLabelInPopOut(item.key) && 'invisible',
                          )}
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {hasAllScores && total != null && (
        <div
          className="flex w-fit items-start gap-1 bg-secondary p-4"
          data-testid="score-weights-formula"
          // biome-ignore lint/a11y/useSemanticElements: a read-only equation, not form controls, so <fieldset> doesn't fit
          role="group"
          aria-label={`${items.map((item) => `${item.score} times ${item.weightPct} percent`).join(' plus ')} equals ${total}`}
        >
          {items.map((item, index) => (
            <React.Fragment key={item.key}>
              {index > 0 && (
                <span className="flex h-12 shrink-0 items-center">
                  <PlusIcon
                    size={12}
                    className={cx(
                      'shrink-0 text-quinary transition-opacity duration-350',
                      isFaded(item.key) && 'opacity-20',
                    )}
                    aria-hidden="true"
                    data-faded={isFaded(item.key) ? 'true' : 'false'}
                  />
                </span>
              )}
              <span
                className={cx(
                  'flex flex-col items-center gap-1 transition-opacity duration-350',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                  isFaded(item.key) && 'opacity-20',
                )}
                role="img"
                aria-label={`${item.label}: ${item.score} times ${item.weightPct} percent`}
                data-testid="score-weights-formula-term"
                data-key={item.key}
                data-faded={isFaded(item.key) ? 'true' : 'false'}
                {...formulaTermHandlers(item.key)}
              >
                <span className="flex items-center gap-1 font-sans text-md text-tertiary">
                  <span>
                    {'('}
                    <span className={item.accentClassName}>{item.score}</span>
                  </span>
                  <XCloseIcon size={10} className="shrink-0 text-quinary" aria-hidden="true" />
                  <span>{`${item.weightPct}%)`}</span>
                </span>
                <Text variant={TextVariant.MonoXs} color={TextColor.Quaternary} showEllipsis={false}>
                  {item.label}
                </Text>
              </span>
            </React.Fragment>
          ))}
          <span className="flex shrink-0 items-center gap-1 self-stretch">
            <span className="font-sans text-sm text-quinary">=</span>
            <span
              className={cx(TOTAL_CLASSES, TOTAL_COLOR_CLASSES[getScoreColorVariant(total)])}
              data-testid="score-weights-formula-total"
            >
              {total}
            </span>
          </span>
        </div>
      )}
    </div>
  );
});

ScoreWeightsPanel.displayName = 'ScoreWeightsPanel';

export { ScoreWeightsPanel };
export type { ScoreWeightsPanelProps };
